<template>
  <v-container fluid class="modern-dashboard pa-4">
    <!-- Header Section -->


    <!-- Date Range Selector -->
    <v-card class="mb-4" elevation="2">
      <v-card-text>
        <v-layout row wrap align-center>
          <v-flex xs12 md3>
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDate"
                  label="Başlangıç Tarihi"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                  outlined
                  dense
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                locale="tr-TR"
                @input="startDateMenu = false; loadData()"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 md3>
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="endDate"
                  label="Bitiş Tarihi"
                  prepend-icon="event"
                  readonly
                  v-on="on"
                  outlined
                  dense
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                locale="tr-TR"
                @input="endDateMenu = false; loadData()"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 md6 class="text-xs-right">
            <v-btn-toggle v-model="selectedPeriod" mandatory @change="changePeriod">
              <v-btn flat value="today">Bugün</v-btn>
              <v-btn flat value="week">Bu Hafta</v-btn>
              <v-btn flat value="month">Bu Ay</v-btn>
              <v-btn flat value="year">Bu Yıl</v-btn>
            </v-btn-toggle>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- KPI Cards Row 1 -->
    <v-layout row wrap class="mb-4">
      <!-- Total Revenue -->
      <v-flex xs12 sm6 md3>
        <v-card class="kpi-card blue darken-1 white--text" elevation="4">
          <v-progress-linear v-if="statsLoading" indeterminate color="white" height="3"></v-progress-linear>
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <v-icon size="48" color="white" class="mr-3">attach_money</v-icon>
              <div class="flex-grow-1">
                <div class="caption mb-1">Toplam Gelir</div>
                <div v-if="statsLoading" class="display-1 font-weight-bold">
                  <v-progress-circular indeterminate color="white" size="24"></v-progress-circular>
                </div>
                <div v-else class="display-1 font-weight-bold">{{ formatMoney(stats.totalRevenue) }}</div>
                <div class="caption mt-1">
                  <v-icon small :color="stats.revenueGrowthPercent >= 0 ? 'green lighten-3' : 'red lighten-3'">
                    {{ stats.revenueGrowthPercent >= 0 ? 'trending_up' : 'trending_down' }}
                  </v-icon>
                  {{ Math.abs(stats.revenueGrowthPercent).toFixed(1) }}% önceki döneme göre
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Total Orders -->
      <v-flex xs12 sm6 md3>
        <v-card class="kpi-card green darken-1 white--text" elevation="4">
          <v-progress-linear v-if="statsLoading" indeterminate color="white" height="3"></v-progress-linear>
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <v-icon size="48" color="white" class="mr-3">shopping_cart</v-icon>
              <div class="flex-grow-1">
                <div class="caption mb-1">Toplam Sipariş</div>
                <div v-if="statsLoading" class="display-1 font-weight-bold">
                  <v-progress-circular indeterminate color="white" size="24"></v-progress-circular>
                </div>
                <div v-else class="display-1 font-weight-bold">{{ formatNumber(stats.totalOrders, 0) }}</div>
                <div class="caption mt-1">
                  <v-icon small :color="stats.ordersGrowthPercent >= 0 ? 'green lighten-3' : 'red lighten-3'">
                    {{ stats.ordersGrowthPercent >= 0 ? 'trending_up' : 'trending_down' }}
                  </v-icon>
                  {{ Math.abs(stats.ordersGrowthPercent).toFixed(1) }}% önceki döneme göre
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Total Grams -->
      <v-flex xs12 sm6 md3>
        <v-card class="kpi-card amber darken-2 white--text" elevation="4">
          <v-progress-linear v-if="statsLoading" indeterminate color="white" height="3"></v-progress-linear>
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <v-icon size="48" color="white" class="mr-3">opacity</v-icon>
              <div class="flex-grow-1">
                <div class="caption mb-1">Toplam Gram</div>
                <div v-if="statsLoading" class="display-1 font-weight-bold">
                  <v-progress-circular indeterminate color="white" size="24"></v-progress-circular>
                </div>
                <div v-else class="display-1 font-weight-bold">{{ formatGrams(stats.totalGrams) }}</div>
                <div class="caption mt-1">
                  <v-icon small :color="stats.gramsGrowthPercent >= 0 ? 'green lighten-3' : 'red lighten-3'">
                    {{ stats.gramsGrowthPercent >= 0 ? 'trending_up' : 'trending_down' }}
                  </v-icon>
                  {{ Math.abs(stats.gramsGrowthPercent).toFixed(1) }}% önceki döneme göre
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Average Order Value -->
      <v-flex xs12 sm6 md3>
        <v-card class="kpi-card purple darken-1 white--text" elevation="4">
          <v-progress-linear v-if="statsLoading" indeterminate color="white" height="3"></v-progress-linear>
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <v-icon size="48" color="white" class="mr-3">analytics</v-icon>
              <div class="flex-grow-1">
                <div class="caption mb-1">Ortalama Sipariş</div>
                <div v-if="statsLoading" class="display-1 font-weight-bold">
                  <v-progress-circular indeterminate color="white" size="24"></v-progress-circular>
                </div>
                <div v-else class="display-1 font-weight-bold">{{ formatMoney(stats.averageOrderValue) }}</div>
                <div class="caption mt-1">Sipariş başına ortalama</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Period Performance Card -->
    <v-card class="mb-4" elevation="3">
      <v-card-title class="indigo white--text">
        <v-icon color="white" left>date_range</v-icon>
        Seçilen Dönem Performansı ({{ formatDate(startDate) }} - {{ formatDate(endDate) }})
      </v-card-title>
      <v-progress-linear v-if="statsLoading" indeterminate color="indigo" height="3"></v-progress-linear>
      <v-card-text class="pa-0">
        <v-layout row wrap>
          <v-flex xs12 sm4 class="text-xs-center pa-4 border-right">
            <div class="period-stat">
              <v-icon size="40" color="blue">attach_money</v-icon>
              <div class="stat-label mt-2">Gelir</div>
              <div v-if="statsLoading" class="stat-value">
                <v-progress-circular indeterminate color="blue" size="24"></v-progress-circular>
              </div>
              <div v-else class="stat-value blue--text">{{ formatMoney(stats.totalRevenue) }}</div>
            </div>
          </v-flex>
          <v-flex xs12 sm4 class="text-xs-center pa-4 border-right">
            <div class="period-stat">
              <v-icon size="40" color="green">shopping_cart</v-icon>
              <div class="stat-label mt-2">Sipariş</div>
              <div v-if="statsLoading" class="stat-value">
                <v-progress-circular indeterminate color="green" size="24"></v-progress-circular>
              </div>
              <div v-else class="stat-value green--text">{{ formatNumber(stats.totalOrders, 0) }}</div>
            </div>
          </v-flex>
          <v-flex xs12 sm4 class="text-xs-center pa-4">
            <div class="period-stat">
              <v-icon size="40" color="amber darken-2">opacity</v-icon>
              <div class="stat-label mt-2">Gram</div>
              <div v-if="statsLoading" class="stat-value">
                <v-progress-circular indeterminate color="amber darken-2" size="24"></v-progress-circular>
              </div>
              <div v-else class="stat-value amber--text text--darken-2">{{ formatGrams(stats.totalGrams) }}</div>
            </div>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Charts Row 1: Main Trends -->
    <v-layout row wrap class="mb-4">
      <!-- Revenue & Orders Trend -->
      <v-flex xs12 md8>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="blue">show_chart</v-icon>
            <span class="font-weight-medium">Gelir ve Sipariş Trendi</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="trendLoading" indeterminate color="blue" size="20"></v-progress-circular>
          </v-card-title>
          <v-card-text style="height: 350px; position: relative;">
            <div v-if="trendLoading" class="loading-overlay">
              <v-progress-circular indeterminate color="blue" size="64"></v-progress-circular>
              <div class="mt-3">Yükleniyor...</div>
            </div>
            <canvas ref="trendChart"></canvas>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Order Status Distribution -->
      <v-flex xs12 md4>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="purple">pie_chart</v-icon>
            <span class="font-weight-medium">Sipariş Durumları</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="statusLoading" indeterminate color="purple" size="20"></v-progress-circular>
          </v-card-title>
          <v-card-text style="height: 350px; position: relative;">
            <div v-if="statusLoading" class="loading-overlay">
              <v-progress-circular indeterminate color="purple" size="64"></v-progress-circular>
              <div class="mt-3">Yükleniyor...</div>
            </div>
            <canvas ref="statusChart"></canvas>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Charts Row 2: Grams & Categories -->
    <v-layout row wrap class="mb-4">
      <!-- Grams Trend -->
      <v-flex xs12 md6>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="amber darken-2">trending_up</v-icon>
            <span class="font-weight-medium">Günlük Gram Trendi</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="trendLoading" indeterminate color="amber darken-2" size="20"></v-progress-circular>
          </v-card-title>
          <v-card-text style="height: 300px; position: relative;">
            <div v-if="trendLoading" class="loading-overlay">
              <v-progress-circular indeterminate color="amber darken-2" size="64"></v-progress-circular>
              <div class="mt-3">Yükleniyor...</div>
            </div>
            <canvas ref="gramsChart"></canvas>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Category Distribution -->
      <v-flex xs12 md6>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="teal">donut_large</v-icon>
            <span class="font-weight-medium">Kategori Bazlı Gelir Dağılımı</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="categoryLoading" indeterminate color="teal" size="20"></v-progress-circular>
          </v-card-title>
          <v-card-text style="height: 300px; position: relative;">
            <div v-if="categoryLoading" class="loading-overlay">
              <v-progress-circular indeterminate color="teal" size="64"></v-progress-circular>
              <div class="mt-3">Yükleniyor...</div>
            </div>
            <canvas ref="categoryChart"></canvas>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Stats Grid -->
    <v-layout row wrap class="mb-4">
      <!-- Category Performance Table -->
      <v-flex xs12 md6>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="deep-orange">category</v-icon>
            <span class="font-weight-medium">Kategori Performansı</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="categoryLoading" indeterminate color="deep-orange" size="20"></v-progress-circular>
          </v-card-title>
          <v-data-table
            :headers="categoryHeaders"
            :items="categoryDistribution"
            :loading="categoryLoading"
            hide-actions
            class="elevation-0"
          >
            <template v-slot:items="props">
              <tr>
                <td class="font-weight-bold">{{ props.item.categoryName }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.revenue) }}</td>
                <td class="text-xs-right">{{ props.item.orderCount }}</td>
                <td class="text-xs-right">
                  <v-chip small :color="getPercentageColor(props.item.percentage)" text-color="white">
                    {{ props.item.percentage.toFixed(1) }}%
                  </v-chip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>

      <!-- Status Performance Table -->
      <v-flex xs12 md6>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="indigo">assignment</v-icon>
            <span class="font-weight-medium">Sipariş Durum Detayı</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="statusLoading" indeterminate color="indigo" size="20"></v-progress-circular>
          </v-card-title>
          <v-data-table
            :headers="statusHeaders"
            :items="orderStatusDistribution"
            :loading="statusLoading"
            hide-actions
            class="elevation-0"
          >
            <template v-slot:items="props">
              <tr>
                <td>
                  <v-chip small :color="getStatusColor(props.item.statusName)" text-color="white">
                    {{ props.item.statusName }}
                  </v-chip>
                </td>
                <td class="text-xs-right font-weight-bold">{{ props.item.count }}</td>
                <td class="text-xs-right">{{ props.item.percentage.toFixed(1) }}%</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Top Products Table -->
    <v-layout row wrap>
      <v-flex xs12>
        <v-card elevation="3">
          <v-card-title class="grey lighten-4">
            <v-icon left color="orange darken-2">star</v-icon>
            <span class="font-weight-medium">En Çok Satan Ürünler (Top 10)</span>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="topProductsLoading" indeterminate color="orange darken-2" size="20"></v-progress-circular>
          </v-card-title>
          <v-data-table
            :headers="productHeaders"
            :items="topProducts"
            :loading="topProductsLoading"
            hide-actions
            class="elevation-0"
          >
            <template v-slot:items="props">
              <tr>
                <td class="text-xs-center">
                  <v-avatar :color="getRankColor(props.index + 1)" size="32">
                    <span class="white--text font-weight-bold">{{ props.index + 1 }}</span>
                  </v-avatar>
                </td>
                <td class="font-weight-medium">{{ props.item.productName }}</td>
                <td class="text-xs-right">
                  <span class="blue--text font-weight-bold">{{ formatMoney(props.item.revenue) }}</span>
                </td>
                <td class="text-xs-right">{{ props.item.soldCount }}</td>
                <td class="text-xs-right">
                  <span class="amber--text text--darken-2 font-weight-bold">{{ formatGrams(props.item.grams) }}</span>
                </td>
                <td class="text-xs-right">
                  <v-progress-linear
                    :value="(props.item.revenue / topProducts[0].revenue) * 100"
                    height="20"
                    :color="getProgressColor(props.index)"
                  >
                    <small class="white--text">{{ ((props.item.revenue / topProducts[0].revenue) * 100).toFixed(0) }}%</small>
                  </v-progress-linear>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Chart from 'chart.js/dist/Chart.js';
import Api from '@/api/baseApi';

export default {
  name: 'ModernDashboard',
  data() {
    const today = new Date();
    const lastMonth = new Date(today);
    lastMonth.setDate(today.getDate() - 30);

    return {
      statsLoading: false,
      trendLoading: false,
      categoryLoading: false,
      topProductsLoading: false,
      statusLoading: false,
      startDate: lastMonth.toISOString().substr(0, 10),
      endDate: today.toISOString().substr(0, 10),
      startDateMenu: false,
      endDateMenu: false,
      selectedPeriod: 'month',
      stats: {
        totalRevenue: 0,
        totalOrders: 0,
        totalGrams: 0,
        averageOrderValue: 0,
        revenueGrowthPercent: 0,
        ordersGrowthPercent: 0,
        gramsGrowthPercent: 0
      },
      dailyTrend: [],
      categoryDistribution: [],
      topProducts: [],
      orderStatusDistribution: [],
      categoryHeaders: [
        { text: 'Kategori', value: 'categoryName', sortable: false },
        { text: 'Gelir (₺)', value: 'revenue', align: 'right', sortable: false },
        { text: 'Sipariş', value: 'orderCount', align: 'right', sortable: false },
        { text: 'Pay (%)', value: 'percentage', align: 'right', sortable: false }
      ],
      statusHeaders: [
        { text: 'Durum', value: 'statusName', sortable: false },
        { text: 'Adet', value: 'count', align: 'right', sortable: false },
        { text: 'Oran (%)', value: 'percentage', align: 'right', sortable: false }
      ],
      productHeaders: [
        { text: '#', value: 'rank', sortable: false, align: 'center' },
        { text: 'Ürün Adı', value: 'productName' },
        { text: 'Gelir (₺)', value: 'revenue', align: 'right' },
        { text: 'Satılan Adet', value: 'soldCount', align: 'right' },
        { text: 'Gram', value: 'grams', align: 'right' },
        { text: 'Performans', value: 'performance', sortable: false, align: 'right' }
      ],
      charts: {
        trend: null,
        grams: null,
        category: null,
        status: null
      }
    };
  },
  async mounted() {
    await this.loadData();
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
  },
  methods: {
    async loadData() {
      // Tüm veri yüklemelerini paralel ve asenkron olarak başlat
      this.loadDashboardStats();
      this.loadDailyTrend();
      this.loadCategoryDistribution();
      this.loadTopProducts();
      this.loadOrderStatusDistribution();
    },
    async loadDashboardStats() {
      this.statsLoading = true;
      try {
        const response = await Api.get(
          `api/reports/GetDashboardStats?startDate=${this.startDate}&endDate=${this.endDate}`
        );
        this.stats = response.data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Dashboard istatistikleri yüklenirken hata:', error);
      } finally {
        this.statsLoading = false;
      }
    },
    async loadDailyTrend() {
      this.trendLoading = true;
      try {
        const response = await Api.get(
          `api/reports/GetDailyTrend?startDate=${this.startDate}&endDate=${this.endDate}`
        );
        this.dailyTrend = response.data;
        this.$nextTick(() => {
          this.renderTrendChart();
          this.renderGramsChart();
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Günlük trend verisi yüklenirken hata:', error);
      } finally {
        this.trendLoading = false;
      }
    },
    async loadCategoryDistribution() {
      this.categoryLoading = true;
      try {
        const response = await Api.get(
          `api/reports/GetCategoryDistribution?startDate=${this.startDate}&endDate=${this.endDate}`
        );
        this.categoryDistribution = response.data;
        this.$nextTick(() => {
          this.renderCategoryChart();
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Kategori dağılımı yüklenirken hata:', error);
      } finally {
        this.categoryLoading = false;
      }
    },
    async loadTopProducts() {
      this.topProductsLoading = true;
      try {
        const response = await Api.get(
          `api/reports/GetTopProducts?startDate=${this.startDate}&endDate=${this.endDate}&topCount=10`
        );
        this.topProducts = response.data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('En çok satan ürünler yüklenirken hata:', error);
      } finally {
        this.topProductsLoading = false;
      }
    },
    async loadOrderStatusDistribution() {
      this.statusLoading = true;
      try {
        const response = await Api.get(
          `api/reports/GetOrderStatusDistribution?startDate=${this.startDate}&endDate=${this.endDate}`
        );
        this.orderStatusDistribution = response.data;
        this.$nextTick(() => {
          this.renderStatusChart();
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Sipariş durum dağılımı yüklenirken hata:', error);
      } finally {
        this.statusLoading = false;
      }
    },
    renderTrendChart() {
      if (this.charts.trend) this.charts.trend.destroy();

      const ctx = this.$refs.trendChart.getContext('2d');
      this.charts.trend = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.dailyTrend.map(d => d.date),
          datasets: [
            {
              label: 'Gelir (₺)',
              data: this.dailyTrend.map(d => d.revenue),
              borderColor: '#1976D2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              yAxisID: 'y-revenue',
              fill: true,
              borderWidth: 3
            },
            {
              label: 'Sipariş Adedi',
              data: this.dailyTrend.map(d => d.orderCount),
              borderColor: '#388E3C',
              backgroundColor: 'rgba(56, 142, 60, 0.1)',
              yAxisID: 'y-orders',
              fill: true,
              borderWidth: 3
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              {
                id: 'y-revenue',
                type: 'linear',
                position: 'left',
                ticks: {
                  callback: (value) => this.formatMoney(value)
                }
              },
              {
                id: 'y-orders',
                type: 'linear',
                position: 'right',
                gridLines: {
                  drawOnChartArea: false
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const label = data.datasets[tooltipItem.datasetIndex].label;
                const value = tooltipItem.yLabel;
                return `${label}: ${tooltipItem.datasetIndex === 0 ? this.formatMoney(value) : value}`;
              }
            }
          }
        }
      });
    },
    renderGramsChart() {
      if (this.charts.grams) this.charts.grams.destroy();

      const ctx = this.$refs.gramsChart.getContext('2d');
      this.charts.grams = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.dailyTrend.map(d => d.date),
          datasets: [
            {
              label: 'Gram',
              data: this.dailyTrend.map(d => d.grams),
              backgroundColor: '#FFA726',
              borderColor: '#F57C00',
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{
              ticks: {
                callback: (value) => this.formatGrams(value)
              }
            }]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => `Gram: ${this.formatGrams(tooltipItem.yLabel)}`
            }
          }
        }
      });
    },
    renderCategoryChart() {
      if (this.charts.category) this.charts.category.destroy();

      const ctx = this.$refs.categoryChart.getContext('2d');
      const colors = [
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#E91E63', '#00BCD4', '#8BC34A', '#FF5722'
      ];

      this.charts.category = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.categoryDistribution.map(c => c.categoryName),
          datasets: [{
            data: this.categoryDistribution.map(c => c.revenue),
            backgroundColor: colors,
            borderWidth: 3,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'right'
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                const item = this.categoryDistribution.find(c => c.categoryName === label);
                const percentage = item ? item.percentage : 0;
                return `${label}: ${this.formatMoney(value)} (${percentage.toFixed(1)}%)`;
              }
            }
          }
        }
      });
    },
    renderStatusChart() {
      if (this.charts.status) this.charts.status.destroy();

      const ctx = this.$refs.statusChart.getContext('2d');
      const colors = [
        '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#607D8B'
      ];

      this.charts.status = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: this.orderStatusDistribution.map(s => s.statusName),
          datasets: [{
            data: this.orderStatusDistribution.map(s => s.count),
            backgroundColor: colors,
            borderWidth: 3,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom'
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                const item = this.orderStatusDistribution.find(s => s.statusName === label);
                const percentage = item ? item.percentage : 0;
                return `${label}: ${value} (${percentage.toFixed(1)}%)`;
              }
            }
          }
        }
      });
    },
    changePeriod(period) {
      const today = new Date();
      let start = new Date(today);

      switch (period) {
        case 'today':
          start = new Date(today);
          break;
        case 'week':
          start.setDate(today.getDate() - 7);
          break;
        case 'month':
          start.setDate(today.getDate() - 30);
          break;
        case 'year':
          start.setFullYear(today.getFullYear() - 1);
          break;
      }

      this.startDate = start.toISOString().substr(0, 10);
      this.endDate = today.toISOString().substr(0, 10);
      this.loadData();
    },
    formatMoney(value) {
      if (!value) return '0,00 ₺';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value) + ' ₺';
    },
    formatGrams(value) {
      if (!value) return '0,00 GR';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value) + ' GR';
    },
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined) return '0';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR');
    },
    getPercentageColor(percentage) {
      if (percentage >= 30) return 'green';
      if (percentage >= 15) return 'orange';
      return 'red';
    },
    getStatusColor(status) {
      const colors = {
        'Yeni': 'blue',
        'Paketleniyor': 'orange',
        'Gönderildi': 'green',
        'Teslim Edildi': 'teal',
        'İptal': 'red',
        'İade': 'purple'
      };
      return colors[status] || 'grey';
    },
    getRankColor(rank) {
      if (rank === 1) return 'amber darken-2';
      if (rank === 2) return 'blue-grey';
      if (rank === 3) return 'deep-orange';
      return 'grey';
    },
    getProgressColor(index) {
      const colors = ['amber darken-2', 'blue', 'green', 'purple', 'pink', 'teal', 'indigo', 'orange', 'cyan', 'lime'];
      return colors[index % colors.length];
    }
  }
};
</script>

<style scoped>
.modern-dashboard {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.dashboard-header {
  text-align: center;
  padding: 20px 0;
  margin-bottom: 10px;
}

/* KPI Cards */
.kpi-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

/* Period Stats */
.period-stat {
  padding: 10px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  margin-top: 8px;
}

.border-right {
  border-right: 1px solid #e0e0e0;
}

/* Card Enhancements */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}

.v-card__title {
  font-size: 16px;
  padding: 12px 16px;
}

.v-card__text {
  position: relative;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
  color: #666;
  font-size: 14px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .border-right {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
}
</style>
